/**
 * Opt-in percent for the tag-rule compare editor. Off: one dashed % button —
 * the operator compares the metrics directly. On: the labeled percent field
 * plus a lit "% of" button; hovering either shows how the level is
 * calculated, clicking the button returns to the direct comparison.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Percent } from 'lucide-react';

import { NumberField } from '@/components/ui';
import { SHOW_COMPARE_PERCENT } from '@/features/flow-optimizer/compare/compare-flags';

export interface PercentFieldProps {
  percent?: number;
  /** Hover text explaining the calculation. */
  calcTitle: string;
  /** Hover text for the inactive % button. */
  addTitle: string;
  onChange: (percent: number | undefined) => void;
}

const PercentField: FC<PercentFieldProps> = ({ percent, calcTitle, addTitle, onChange }) => {
  if (percent === undefined) {
    // The % is currently hidden (see compare-flags) — the direct comparison
    // is the whole feature until the flag flips back.
    if (!SHOW_COMPARE_PERCENT) return null;
    return (
      <button
        type="button"
        onClick={() => onChange(100)}
        title={addTitle}
        aria-label="Add a percent"
        className="mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center self-end rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
      >
        <Percent className="h-4 w-4" />
      </button>
    );
  }
  return (
    <>
      <div className="w-[88px]" title={calcTitle}>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Percent
        </label>
        <NumberField
          value={percent}
          min={0}
          onChange={onChange}
          aria-label="Compare percent"
          className="h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
      <button
        type="button"
        onClick={() => onChange(undefined)}
        title={`${calcTitle}\n\nClick to remove the % and compare the metrics directly.`}
        aria-label="Remove the percent"
        className="mb-1 flex h-7 shrink-0 items-center gap-0.5 self-end rounded-md bg-primary/15 px-1.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/25"
      >
        <Percent className="h-3 w-3" />
        of
      </button>
    </>
  );
};

export default PercentField;
